/**
 * Format de dessin du schéma corporel, et sa (dé)sérialisation. Le serveur le stocke sans
 * l'interpréter, mais l'imprime dans l'export du dossier : l'écran et le PDF lisent donc la même
 * définition, ici, plutôt que chacun la sienne.
 *
 * Trois choix rendent le format durable :
 * — les coordonnées sont **normalisées** entre 0 et 1 dans un cadre de 4 sur 3, celui de la V1
 *   (800 × 600) : un dessin fait sur un grand écran se relit identique sur une tablette ou sur le
 *   papier ;
 * — la couleur est un **jeton** sémantique, pas une valeur : le dessin suit le thème clair ou
 *   sombre sans conversion ;
 * — les épaisseurs sont exprimées pour un cadre de 800 pixels de large et remises à l'échelle du
 *   cadre réel, pour que le trait garde la même allure quelle que soit la taille d'affichage.
 *
 * Depuis le cumul avec `react-muscle-highlighter` (1.2.0, MIT), le même objet porte aussi les
 * muscles surlignés. Ils vivent à côté des traits, pas à leur place : un document déjà enregistré
 * sans clé `muscles` reste un dessin valide, aux muscles vides. Pas de colonne nouvelle : c'est
 * le `drawingData` déjà stocké par vue. La bibliothèque ne connaît que la face et le dos.
 *
 * Fonctions pures : aucune ne touche au DOM.
 */

import type { BodyDiagramView } from './enums.js';

export const bodyDiagramColorTokens = [
  'chart-1',
  'chart-2',
  'chart-3',
  'chart-4',
  'chart-5',
  'foreground',
] as const;
export type BodyDiagramColorToken = (typeof bodyDiagramColorTokens)[number];

export const bodyDiagramStrokeWidths = [2, 4, 8] as const;
export type BodyDiagramStrokeWidth = (typeof bodyDiagramStrokeWidths)[number];

export const bodyDiagramShapeKinds = ['circle', 'rectangle', 'arrow'] as const;
export type BodyDiagramShapeKind = (typeof bodyDiagramShapeKinds)[number];

/** Largeur sur hauteur du cadre de dessin. */
export const BODY_DIAGRAM_ASPECT = 4 / 3;
/** Largeur de référence des épaisseurs de trait, en pixels. */
export const BODY_DIAGRAM_REFERENCE_WIDTH = 800;
/** Hauteur d'un texte, en fraction de la hauteur du cadre. */
export const BODY_DIAGRAM_TEXT_SIZE = 0.035;
export const BODY_DIAGRAM_TEXT_MAX_LENGTH = 120;

export interface BodyDiagramPoint {
  /** Abscisse normalisée, entre 0 et 1. */
  x: number;
  y: number;
}

export interface BodyDiagramStroke {
  kind: 'stroke';
  color: BodyDiagramColorToken;
  width: BodyDiagramStrokeWidth;
  /** Trait de gomme : effacé à la composition, jamais une couleur de fond posée par-dessus. */
  eraser: boolean;
  points: BodyDiagramPoint[];
}

/** Cercle (centre `from`, rayon jusqu'à `to`), rectangle (deux coins) ou flèche (de `from` à `to`). */
export interface BodyDiagramShape {
  kind: BodyDiagramShapeKind;
  color: BodyDiagramColorToken;
  width: BodyDiagramStrokeWidth;
  from: BodyDiagramPoint;
  to: BodyDiagramPoint;
}

export interface BodyDiagramText {
  kind: 'text';
  color: BodyDiagramColorToken;
  /** Coin haut gauche du texte. */
  at: BodyDiagramPoint;
  text: string;
}

export type BodyDiagramItem = BodyDiagramStroke | BodyDiagramShape | BodyDiagramText;

/**
 * Slugs de `react-muscle-highlighter` 1.2.0, recopiés ici pour que le format ne dépende pas de
 * React. Il n'y a pas de vue de profil dans cette liste.
 */
export const bodyDiagramMuscleSlugs = [
  'abs',
  'adductors',
  'ankles',
  'biceps',
  'calves',
  'chest',
  'deltoids',
  'feet',
  'forearm',
  'gluteal',
  'hamstring',
  'hands',
  'hair',
  'head',
  'knees',
  'lower-back',
  'neck',
  'obliques',
  'quadriceps',
  'tibialis',
  'trapezius',
  'triceps',
  'upper-back',
] as const;
export type BodyDiagramMuscleSlug = (typeof bodyDiagramMuscleSlugs)[number];

export const bodyDiagramMuscleSides = ['left', 'right'] as const;
export type BodyDiagramMuscleSide = (typeof bodyDiagramMuscleSides)[number];

/** Un muscle surligné. `side` manque quand le clic concerne le muscle entier. */
export interface BodyDiagramMuscle {
  slug: BodyDiagramMuscleSlug;
  side?: BodyDiagramMuscleSide;
  color: BodyDiagramColorToken;
}

export interface BodyDiagramDrawing {
  version: 2;
  items: BodyDiagramItem[];
  muscles: BodyDiagramMuscle[];
}

export const EMPTY_BODY_DIAGRAM: BodyDiagramDrawing = { version: 2, items: [], muscles: [] };

/** La bibliothèque n'affiche que la face et le dos. Les profils restent un dessin. */
export function bodyDiagramViewHasMuscles(view: BodyDiagramView): view is 'front' | 'back' {
  return view === 'front' || view === 'back';
}

/**
 * Cadre de la première version : un portrait d'une largeur pour deux de hauteur. Ses traits sont
 * replacés dans la bande centrale du cadre de 4 sur 3, à hauteur égale, sans déformation.
 */
const LEGACY_WIDTH = 0.5 / BODY_DIAGRAM_ASPECT;
const LEGACY_OFFSET = (1 - LEGACY_WIDTH) / 2;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isColorToken(value: unknown): value is BodyDiagramColorToken {
  return typeof value === 'string' && (bodyDiagramColorTokens as readonly string[]).includes(value);
}

function isStrokeWidth(value: unknown): value is BodyDiagramStrokeWidth {
  return (
    typeof value === 'number' && (bodyDiagramStrokeWidths as readonly number[]).includes(value)
  );
}

function isShapeKind(value: unknown): value is BodyDiagramShapeKind {
  return typeof value === 'string' && (bodyDiagramShapeKinds as readonly string[]).includes(value);
}

function isMuscleSlug(value: unknown): value is BodyDiagramMuscleSlug {
  return typeof value === 'string' && (bodyDiagramMuscleSlugs as readonly string[]).includes(value);
}

function isMuscleSide(value: unknown): value is BodyDiagramMuscleSide {
  return typeof value === 'string' && (bodyDiagramMuscleSides as readonly string[]).includes(value);
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

function parsePoint(value: unknown): BodyDiagramPoint | null {
  if (!isRecord(value)) return null;
  const { x, y } = value;
  if (typeof x !== 'number' || typeof y !== 'number') return null;
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  return { x: clamp01(x), y: clamp01(y) };
}

function parseStroke(value: Record<string, unknown>): BodyDiagramStroke | null {
  if (!isColorToken(value.color) || !isStrokeWidth(value.width)) return null;
  if (!Array.isArray(value.points)) return null;
  const points = value.points.flatMap((point) => {
    const parsed = parsePoint(point);
    return parsed === null ? [] : [parsed];
  });
  // Un trait d'un seul point n'est pas un trait : il vient d'un clic sans déplacement.
  if (points.length < 2) return null;
  return {
    kind: 'stroke',
    color: value.color,
    width: value.width,
    eraser: value.eraser === true,
    points,
  };
}

function parseShape(value: Record<string, unknown>): BodyDiagramShape | null {
  if (!isShapeKind(value.kind) || !isColorToken(value.color) || !isStrokeWidth(value.width)) {
    return null;
  }
  const from = parsePoint(value.from);
  const to = parsePoint(value.to);
  if (from === null || to === null) return null;
  if (from.x === to.x && from.y === to.y) return null;
  return { kind: value.kind, color: value.color, width: value.width, from, to };
}

function parseText(value: Record<string, unknown>): BodyDiagramText | null {
  if (!isColorToken(value.color) || typeof value.text !== 'string') return null;
  const at = parsePoint(value.at);
  const text = value.text.trim().slice(0, BODY_DIAGRAM_TEXT_MAX_LENGTH);
  if (at === null || text === '') return null;
  return { kind: 'text', color: value.color, at, text };
}

function parseMuscle(value: unknown): BodyDiagramMuscle | null {
  if (!isRecord(value) || !isMuscleSlug(value.slug) || !isColorToken(value.color)) return null;
  if (value.side === undefined) return { slug: value.slug, color: value.color };
  if (!isMuscleSide(value.side)) return null;
  return { slug: value.slug, color: value.color, side: value.side };
}

function parseMuscles(data: Record<string, unknown>): BodyDiagramMuscle[] {
  if (!Array.isArray(data.muscles)) return [];
  return collect(data.muscles, parseMuscle).slice(0, bodyDiagramMuscleSlugs.length * 2);
}

function parseItem(value: unknown): BodyDiagramItem | null {
  if (!isRecord(value)) return null;
  if (value.kind === 'stroke') return parseStroke(value);
  if (value.kind === 'text') return parseText(value);
  return parseShape(value);
}

function fromLegacy(stroke: BodyDiagramStroke): BodyDiagramStroke {
  return {
    ...stroke,
    points: stroke.points.map((point) => ({
      x: LEGACY_OFFSET + point.x * LEGACY_WIDTH,
      y: point.y,
    })),
  };
}

function collect<T>(values: unknown[], parse: (value: unknown) => T | null): T[] {
  return values.flatMap((value) => {
    const parsed = parse(value);
    return parsed === null ? [] : [parsed];
  });
}

/**
 * Lecture d'un `drawingData`. Le contenu est opaque pour le serveur : tout ce qui n'est pas
 * reconnu ici est écarté sans faire échouer l'écran ni l'export. La première version, une simple
 * liste de traits dans un cadre portrait, est convertie à la lecture.
 */
export function parseBodyDiagram(data: Record<string, unknown> | undefined): BodyDiagramDrawing {
  if (data === undefined) return EMPTY_BODY_DIAGRAM;
  if (Array.isArray(data.items)) {
    return { version: 2, items: collect(data.items, parseItem), muscles: parseMuscles(data) };
  }
  if (Array.isArray(data.strokes)) {
    const strokes = collect(data.strokes, (value) => (isRecord(value) ? parseStroke(value) : null));
    return { version: 2, items: strokes.map(fromLegacy), muscles: [] };
  }
  return EMPTY_BODY_DIAGRAM;
}

function sameMuscle(
  muscle: BodyDiagramMuscle,
  mark: Pick<BodyDiagramMuscle, 'slug' | 'side'>,
): boolean {
  return muscle.slug === mark.slug && muscle.side === mark.side;
}

/**
 * Ajoute le muscle, change sa couleur, ou le retire s'il est déjà de cette couleur.
 * Les traits ne bougent pas.
 */
export function toggleBodyDiagramMuscle(
  drawing: BodyDiagramDrawing,
  mark: BodyDiagramMuscle,
): BodyDiagramDrawing {
  const current = drawing.muscles.find((muscle) => sameMuscle(muscle, mark));
  const muscles =
    current === undefined
      ? [...drawing.muscles, mark]
      : current.color === mark.color
        ? drawing.muscles.filter((muscle) => !sameMuscle(muscle, mark))
        : drawing.muscles.map((muscle) => (sameMuscle(muscle, mark) ? mark : muscle));
  return { version: 2, items: drawing.items, muscles };
}

/** Trois décimales : la précision d'un pixel sur un écran de 2 000 points, sans le poids. */
function round(point: BodyDiagramPoint): BodyDiagramPoint {
  return { x: Number(point.x.toFixed(3)), y: Number(point.y.toFixed(3)) };
}

function serializeItem(item: BodyDiagramItem): Record<string, unknown> {
  switch (item.kind) {
    case 'stroke':
      return {
        kind: 'stroke',
        color: item.color,
        width: item.width,
        eraser: item.eraser,
        points: item.points.map(round),
      };
    case 'text':
      return { kind: 'text', color: item.color, at: round(item.at), text: item.text };
    default:
      return {
        kind: item.kind,
        color: item.color,
        width: item.width,
        from: round(item.from),
        to: round(item.to),
      };
  }
}

function serializeMuscle(muscle: BodyDiagramMuscle): Record<string, unknown> {
  return muscle.side === undefined
    ? { slug: muscle.slug, color: muscle.color }
    : { slug: muscle.slug, color: muscle.color, side: muscle.side };
}

/** Écriture : la forme envoyée au serveur, un objet JSON de moins de 256 ko. */
export function serializeBodyDiagram(drawing: BodyDiagramDrawing): Record<string, unknown> {
  return {
    version: 2,
    items: drawing.items.map(serializeItem),
    muscles: drawing.muscles.map(serializeMuscle),
  };
}
